/**
 * Shared index-tick hub.
 *
 * One socket listener and one flush timer for the whole app. Ticks are coalesced per
 * symbol and published to subscribers outside of React, so consumers can update a chart
 * instance or a single DOM node without re-rendering their tree.
 *
 * Consumers:
 *  - `useIndexTickFeed()`   attach the feed (ref-counted, safe to call from many components)
 *  - `subscribeIndexTick()` imperative subscription (charts, DOM cells)
 *  - `useIndexTick()`       React subscription for small leaf components
 *  - `getIndexTick()`       latest snapshot, for seeding on mount
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";
import type { Socket } from "socket.io-client";
import { useSocket } from "src/components/SocketContext";
import {
  indexKeyVariants,
  normalizeIndexKey,
} from "src/constants/indexSymbols";
import { fetchWrapper } from "src/helpers";

export interface IndexTickSnapshot {
  symbol: string;
  name: string;
  token: string;
  ltp: number;
  change: number;
  percentChange: number;
  /**
   * OHLC/volume are optional: the live feed does not publish them today, and the
   * `index_ticks` columns default to 0. `0` is therefore read as "not reported".
   */
  open?: number;
  high?: number;
  low?: number;
  close?: number;
  volume?: number;
  high_52week?: number;
  timestamp?: string;
}

export type IndexTickListener = (tick: IndexTickSnapshot) => void;

/** Raw payload shape, tolerant of both the socket feed and the REST prime. */
interface RawIndexTick {
  symbol?: string;
  name?: string;
  token?: string;
  ltp?: number | string;
  price?: number | string;
  open?: number | string;
  high?: number | string;
  low?: number | string;
  close?: number | string;
  volume?: number | string;
  high_52week?: number | string;
  fiftyTwoWeekHigh?: number | string;
  change?: number | string;
  percentChange?: number | string;
  percent_change?: number | string;
  timestamp?: string;
  updated_at?: string | Date;
}

/** Coalescing window: at most one publish per symbol per interval. */
export const INDEX_TICK_FLUSH_MS = 250;

const INDEX_TICK_EVENTS = ["index_price_tick", "VIX_PRICE_TICK"] as const;

const store = new Map<string, IndexTickSnapshot>();
const listeners = new Map<string, Set<IndexTickListener>>();
const pending = new Map<string, { tick: IndexTickSnapshot; keys: string[] }>();

let flushTimer: ReturnType<typeof setTimeout> | null = null;

const toNumber = (value: unknown): number => {
  const num = typeof value === "number" ? value : parseFloat(String(value));
  return Number.isFinite(num) ? num : 0;
};

/** 0 / missing means "not reported by the feed" for OHLC and volume. */
const toOptionalNumber = (value: unknown): number | undefined => {
  const num = toNumber(value);
  return num === 0 ? undefined : num;
};

const toSnapshot = (raw: RawIndexTick): IndexTickSnapshot | null => {
  const symbol = String(raw.symbol ?? raw.name ?? "");
  const ltp = toNumber(raw.ltp ?? raw.price);
  if (!symbol || !ltp) return null;

  return {
    symbol,
    name: String(raw.name ?? symbol),
    token: String(raw.token ?? ""),
    ltp,
    change: toNumber(raw.change),
    percentChange: toNumber(raw.percentChange ?? raw.percent_change),
    open: toOptionalNumber(raw.open),
    high: toOptionalNumber(raw.high),
    low: toOptionalNumber(raw.low),
    close: toOptionalNumber(raw.close),
    volume: toOptionalNumber(raw.volume),
    high_52week: toOptionalNumber(raw.high_52week ?? raw.fiftyTwoWeekHigh),
    timestamp: raw.timestamp
      ? String(raw.timestamp)
      : raw.updated_at
        ? new Date(raw.updated_at).toISOString()
        : undefined,
  };
};

const flush = (): void => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pending.size === 0) return;

  const batch = [...pending.values()];
  pending.clear();

  for (const { tick, keys } of batch) {
    for (const key of keys) {
      // Store and notify together: `useIndexTick` relies on a snapshot that only
      // changes alongside a subscriber notification.
      store.set(key, tick);
    }
    const notified = new Set<IndexTickListener>();
    for (const key of keys) {
      const keyListeners = listeners.get(key);
      if (!keyListeners) continue;
      for (const listener of keyListeners) {
        if (notified.has(listener)) continue;
        notified.add(listener);
        try {
          listener(tick);
        } catch (err) {
          // One bad subscriber must not stop the feed for the others.
          console.error("Index tick listener failed:", err);
        }
      }
    }
  }
};

const ingest = (raw: RawIndexTick, immediate = false): void => {
  const tick = toSnapshot(raw);
  if (!tick) return;

  const keys = indexKeyVariants(tick.token, tick.symbol, tick.name);
  if (keys.length === 0) return;

  pending.set(keys[0], { tick, keys });

  if (immediate) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, INDEX_TICK_FLUSH_MS);
  }
};

const onSocketTick = (raw: RawIndexTick): void => ingest(raw);

/** Latest known tick for any spelling of an index name/symbol/token. */
export const getIndexTick = (
  key: string | null | undefined,
): IndexTickSnapshot | undefined => {
  const normalized = normalizeIndexKey(key);
  return normalized ? store.get(normalized) : undefined;
};

export const subscribeIndexTick = (
  key: string | null | undefined,
  listener: IndexTickListener,
): (() => void) => {
  const normalized = normalizeIndexKey(key);
  if (!normalized) return () => {};

  let keyListeners = listeners.get(normalized);
  if (!keyListeners) {
    keyListeners = new Set();
    listeners.set(normalized, keyListeners);
  }
  keyListeners.add(listener);

  return () => {
    const current = listeners.get(normalized);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(normalized);
  };
};

// ── Feed attachment ──────────────────────────────────────────
let attachedSocket: Socket | null = null;
let feedRefCount = 0;

const detachFeed = (): void => {
  if (!attachedSocket) return;
  for (const event of INDEX_TICK_EVENTS) {
    attachedSocket.off(event, onSocketTick);
  }
  attachedSocket = null;
};

/** Idempotent per socket instance, so a replaced socket is re-bound automatically. */
const attachFeed = (socket: Socket): void => {
  if (attachedSocket === socket) return;
  detachFeed();
  for (const event of INDEX_TICK_EVENTS) {
    socket.on(event, onSocketTick);
  }
  attachedSocket = socket;
};

export const useIndexTickFeed = (): void => {
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    feedRefCount += 1;
    attachFeed(socket);

    return () => {
      feedRefCount -= 1;
      if (feedRefCount === 0) detachFeed();
    };
  }, [socket]);
};

// ── REST prime ────────────────────────────────────────────────
/**
 * Seeds the hub from `index_ticks`, which the ticker service flushes every 30s. This is
 * what makes prices show up on first load, and it is the only source when the socket is
 * down — subscribers are notified as soon as it lands, exactly like a socket tick.
 */
const PRIME_TTL_MS = 30_000;

let primePromise: Promise<void> | null = null;
let primedAt = 0;

export const ensureIndexTicksPrimed = (
  token: string | undefined | null,
): void => {
  if (!token || primePromise) return;
  if (primedAt && Date.now() - primedAt < PRIME_TTL_MS) return;

  primePromise = fetchWrapper
    .get("/api/stocks/index-ticks", undefined, {
      Authorization: `Bearer ${token}`,
    })
    .then((res) => {
      const data = res?.data;
      if (!res?.success || !data) return;

      const items: RawIndexTick[] = [
        ...(data.mainIndices ?? []),
        ...(data.sectors ?? []),
      ];
      for (const item of items) ingest(item, true);
      primedAt = Date.now();
    })
    .catch((err) => {
      console.error("Failed to prime index ticks:", err);
    })
    .finally(() => {
      // Cleared either way, so a failed prime is retried on the next mount.
      primePromise = null;
    });
};

/** React subscription for leaf components that render a single live value. */
export const useIndexTick = (
  key: string | null | undefined,
): IndexTickSnapshot | undefined => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => subscribeIndexTick(key, onStoreChange),
    [key],
  );
  const getSnapshot = useCallback(() => getIndexTick(key), [key]);

  return useSyncExternalStore(subscribe, getSnapshot, () => undefined);
};
